<template>
  <div class="md:mt-[100px]">
    <!-- Hero Section -->
    <div class="main-banner">
      <div class="main-banner-content">
        <div class="banner-text">
          <div class="banner-title">
            {{ $t('chathub.hero.title') }}
          </div>
          <div class="banner-description">
            {{ $t('chathub.hero.subtitle') }}
          </div>
          <div class="banner-description mt-4">
            {{ $t('chathub.hero.description') }}
          </div>
          <div class="banner-action">
            <button @click="connectToHub" class="nav-link button filled" :disabled="isConnecting">
              <p v-if="!isConnecting">{{ $t('chathub.actions.connectNow') }}</p>
              <p v-else>{{ $t('chathub.actions.connecting') }}</p>
            </button>
            <button @click="showDemoCode" class="button outlined">
              <p>{{ $t('chathub.actions.viewDemoCode') }}</p>
            </button>
          </div>
        </div>
        <div class="banner-image-container p-4">
          <div class="chat-demo">
            <div class="chat-header">
              <div class="chat-status" :class="{ 'connected': isConnected, 'disconnected': !isConnected }">
                <span class="status-dot"></span>
                {{ isConnected ? $t('chathub.status.connected') : $t('chathub.status.disconnected') }}
              </div>
            </div>
            <div class="chat-messages" ref="chatMessages">
              <div v-for="message in demoMessages" :key="message.id" class="message" :class="message.type">
                <div class="message-content">
                  <div class="message-text">{{ message.text }}</div>
                  <div class="message-time">{{ message.time }}</div>
                </div>
              </div>
            </div>
            <div class="chat-input">
              <input v-model="newMessage" @keyup.enter="sendMessage" :placeholder="$t('chathub.input.placeholder')"
                :disabled="!isConnected" />
              <button @click="sendMessage" :disabled="!isConnected || !newMessage.trim()">
                {{ $t('chathub.input.send') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- What is Chathub Section -->
    <div class="page-section">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('chathub.whatIs.title') }}</div>
        </div>
        <div class="section-content">
          <div class="text-content mb-8">
            <p>{{ $t('chathub.whatIs.description') }}</p>
          </div>

          <div class="features-grid">
            <div v-for="(feature, index) in tm('chathub.whatIs.features')" :key="index" class="feature-card">
              <div class="feature-icon">
                <NuxtImg :src="rt(feature.icon)" :alt="rt(feature.title)" :width="100" :height="100" format="webp" loading="lazy" />
              </div>
              <h3 class="feature-title">{{ rt(feature.title) }}</h3>
              <p class="feature-description">{{ rt(feature.description) }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Chat Sources Section -->
    <div class="page-section bg-gray-50">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('chathub.sources.title') }}</div>
        </div>
        <div class="section-content">
          <div class="text-content mb-8">
            <p>{{ $t('chathub.sources.description') }}</p>
          </div>

          <div class="sources-grid">
            <div v-for="(source, index) in chatSources" :key="index" class="source-card"
              :class="{ 'coming-soon': source.comingSoon }">
              <div class="source-icon">
                <NuxtImg :src="``" :alt="source.name" :width="24" :height="24" format="webp" loading="lazy" />
              </div>
              <h3 class="source-name">{{ source.name }}</h3>
              <div class="source-status">
                <span v-if="source.comingSoon" class="status-badge coming-soon">{{ $t('chathub.sources.comingSoon')
                }}</span>
                <span v-else class="status-badge available">{{ $t('chathub.sources.available') }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- API Examples Section -->
    <div class="page-section">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('chathub.api.title') }}</div>
        </div>
        <div class="section-content">
          <div class="text-content mb-8">
            <p>{{ $t('chathub.api.description') }}</p>
          </div>

          <div class="api-examples">
            <div class="api-tabs">
              <button v-for="(example, index) in apiExamples" :key="index" @click="activeExample = index"
                class="api-tab" :class="{ 'active': activeExample === index }">
                {{ example.title }}
              </button>
            </div>

            <div class="api-content">
              <div class="api-description">
                <h4>{{ apiExamples[activeExample].title }}</h4>
                <p>{{ apiExamples[activeExample].description }}</p>
              </div>

              <div class="code-block">
                <div class="code-header">
                  <span class="code-language">{{ apiExamples[activeExample].language }}</span>
                  <button @click="copyCode" class="copy-button">
                    {{ $t('chathub.api.copyCode') }}
                  </button>
                </div>
                <pre><code>{{ apiExamples[activeExample].code }}</code></pre>
              </div>

              <div class="api-actions">
                <button @click="downloadExample" class="button outlined">
                  {{ $t('chathub.api.downloadExample') }}
                </button>
                <button @click="testExample" class="button filled">
                  {{ $t('chathub.api.testExample') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Integration Steps Section -->
    <div class="page-section">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('chathub.integration.title') }}</div>
        </div>
        <div class="section-content">
          <div class="integration-steps">
            <div v-for="(step, index) in tm('chathub.integration.steps')" :key="index" class="step-item">
              <div class="step-number">{{ Number(index) + 1 }}</div>
              <div class="step-content">
                <h3 class="step-title">{{ rt(step.title) }}</h3>
                <p class="step-description">{{ rt(step.description) }}</p>
                <div v-if="step.code" class="step-code">
                  <pre><code>{{ rt(step.code) }}</code></pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pricing Section -->
    <div class="page-section bg-gray-50">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('chathub.pricing.title') }}</div>
          <div class="section-head-description text-center">{{ $t('chathub.pricing.subtitle') }}</div>
        </div>
        <div class="section-content">
          <div class="pricing-grid">
            <div v-for="(plan, index) in tm('chathub.pricing.plans')" :key="index" class="pricing-card"
              :class="{ 'popular': plan.popular }">
              <div v-if="plan.popular" class="popular-badge">{{ $t('global.mostPopular') }}</div>
              <div class="plan-header">
                <h3 class="plan-name">{{ rt(plan.name) }}</h3>
                <div class="plan-price">
                  <span class="price">{{ rt(plan.price) }}</span>
                  <span class="period">{{ rt(plan.period) }}</span>
                </div>
                <p class="plan-description">{{ rt(plan.description) }}</p>
              </div>
              <div class="plan-features">
                <ul>
                  <li v-for="(feature, featureIndex) in plan.features" :key="featureIndex">
                    {{ rt(feature) }}
                  </li>
                </ul>
              </div>
              <div class="plan-footer">
                <button class="button" :class="plan.popular ? 'filled' : 'outlined'">
                  {{ rt(plan.button) }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- FAQ Section -->
    <div class="page-section">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('chathub.pricing.faq.title') }}</div>
        </div>
        <div class="section-content">
          <div class="faq-list">
            <div v-for="(faq, index) in tm('chathub.pricing.faq.questions')" :key="index" class="faq-item">
              <div class="faq-question">
                <h4>{{ rt(faq.question) }}</h4>
                <span class="faq-toggle">+</span>
              </div>
              <div class="faq-answer">
                <p>{{ rt(faq.answer) }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- CTA Section -->
    <div class="page-section bg-gradient bottom-banner">
      <div class="container">
        <div class="section-content text-center">
          <div class="cta-content">
            <h2 class="section-head-title mb-4">{{ $t('chathub.cta.title') }}</h2>
            <p class="text-gray-600 mb-8">{{ $t('chathub.cta.description') }}</p>
            <div class="banner-action flex flex-wrap gap-4 justify-center items-center">
              <NuxtLink :to="localePath('/contacts')" class="nav-link button filled">
                <p>{{ $t('chathub.cta.getStarted') }}</p>
              </NuxtLink>
              <button @click="downloadSDK" class="button outlined">
                <p>{{ $t('chathub.cta.downloadSDK') }}</p>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Demo Code Modal -->
    <div v-if="showModal" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>{{ $t('chathub.modal.title') }}</h3>
          <button @click="closeModal" class="close-button">&times;</button>
        </div>
        <div class="modal-body">
          <div class="demo-code">
            <pre><code>{{ demoCode }}</code></pre>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="copyDemoCode" class="button filled">
            {{ $t('chathub.modal.copyCode') }}
          </button>
          <button @click="downloadDemoCode" class="button outlined">
            {{ $t('chathub.modal.downloadCode') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">


const localePath = useLocalePath();
const { t } = useI18n();
const { tm, rt } = useI18n() as any

// Reactive data
const isConnecting = ref(false);
const isConnected = ref(false);
const newMessage = ref('');
const showModal = ref(false);
const activeExample = ref(0);
const chatMessages = ref<HTMLElement>();

// Demo messages
const demoMessages = ref([
  {
    id: 1,
    text: t('chathub.demo.welcomeMessage'),
    type: 'system',
    time: '10:30'
  },
  {
    id: 2,
    text: t('chathub.demo.userMessage'),
    type: 'user',
    time: '10:31'
  },
  {
    id: 3,
    text: t('chathub.demo.botResponse'),
    type: 'bot',
    time: '10:31'
  }
]);

// Chat sources
const chatSources = ref([
  { name: 'Viber', icon: '/images/icons/viber-icon.svg', comingSoon: false },
  { name: 'Viber Bot', icon: '/images/icons/viber-bot-icon.svg', comingSoon: false },
  { name: 'Telegram Bot', icon: '/images/icons/telegram-icon.svg', comingSoon: false },
  { name: 'WhatsApp', icon: '/images/icons/whatsapp-icon.svg', comingSoon: false },
  { name: 'Widget', icon: '/images/icons/widget-icon.svg', comingSoon: false },
  { name: 'Instagram', icon: '/images/icons/instagram-icon.svg', comingSoon: false },
  { name: 'Facebook', icon: '/images/icons/facebook-icon-blue.svg', comingSoon: false },
  { name: 'Rozetka', icon: '/images/icons/rozetka-icon.svg', comingSoon: false },
  { name: 'Prom.ua', icon: '/images/icons/prom-icon.svg', comingSoon: false },
  { name: 'Amazon', icon: '/images/icons/amazon-icon.svg', comingSoon: true },
  { name: 'Etsy', icon: '/images/icons/etsy-icon.svg', comingSoon: true },
  { name: 'TikTok', icon: '/images/icons/tiktok-icon.svg', comingSoon: true },
  { name: 'Twitter', icon: '/images/icons/twitter-icon.svg', comingSoon: true }
]);

// API Examples
const apiExamples = ref([
  {
    title: t('chathub.api.getToken.title'),
    description: t('chathub.api.getToken.description'),
    language: 'JavaScript',
    code: `// Get authentication token
const response = await fetch('https://chatapi.smsbat.com/api/company/get-token', {
    method: 'POST',
    headers: {
        'Content-Type': 'application/json'
    },
    body: JSON.stringify({
        login: 'your_login',
        password: 'your_password'
    })
});

const tokenData = await response.json();
const token = tokenData.token;`
  },
  {
    title: t('chathub.api.connect.title'),
    description: t('chathub.api.connect.description'),
    language: 'JavaScript',
    code: `// Connect to Chathub
const connection = new signalR.HubConnectionBuilder()
    .withUrl("https://chatapi.smsbat.com/chathub", {
        accessTokenFactory: () => token
    })
    .build();

connection.start()
    .then(() => console.log("Connected to ChatHub"))
    .catch(err => console.error("Error connecting to ChatHub:", err));`
  },
  {
    title: t('chathub.api.sendMessage.title'),
    description: t('chathub.api.sendMessage.description'),
    language: 'JavaScript',
    code: `// Send text message
connection.invoke("SendMessage", chatId, "Your message")
    .then(() => console.log("Message sent"))
    .catch(err => console.error("Error sending message:", err));

// Send media message
const mediaDTO = {
    Name: "image.jpg",
    Format: "jpg",
    DataBase64: "base64EncodedString",
    Type: 1 // MediaType.Photo
};

connection.invoke("SendMediaMessage", chatId, mediaDTO)
    .then(() => console.log("Media message sent"))
    .catch(err => console.error("Error sending media message:", err));`
  },
  {
    title: t('chathub.api.receiveUpdates.title'),
    description: t('chathub.api.receiveUpdates.description'),
    language: 'JavaScript',
    code: `// Listen for text messages
connection.on("ReceiveMessage", (message) => {
    console.log("New message received:", message);
    // Handle incoming message
});

// Listen for media messages
connection.on("ReceiveMediaMessage", (mediaData) => {
    console.log("New media message received:", mediaData);
    // Handle incoming media message
});

// Listen for chat status changes
connection.on("ChatStatusChanged", (chatId, status) => {
    console.log(\`Chat \${chatId} status changed to \${status}\`);
});

// Listen for message status changes
connection.on("MessageStatusChanged", (messageId, status) => {
    console.log(\`Message \${messageId} status changed to \${status}\`);
});`
  }
]);

// Demo code
const demoCode = ref(`// Complete Chathub API Integration Example
class ChathubClient {
    constructor() {
        this.connection = null;
        this.token = null;
    }
    
    // Step 1: Get authentication token
    async getToken(login, password) {
        const response = await fetch('https://chatapi.smsbat.com/api/company/get-token', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ login, password })
        });
        
        const data = await response.json();
        this.token = data.token;
        return this.token;
    }
    
    // Step 2: Connect to Chathub
    async connect() {
        this.connection = new signalR.HubConnectionBuilder()
            .withUrl("https://chatapi.smsbat.com/chathub", {
                accessTokenFactory: () => this.token
            })
            .build();
            
        // Set up event listeners
        this.setupEventListeners();
        
        await this.connection.start();
        console.log("Connected to Chathub!");
    }
    
    // Step 3: Set up event listeners
    setupEventListeners() {
        // Listen for text messages
        this.connection.on("ReceiveMessage", (message) => {
            console.log("New message:", message);
            this.handleTextMessage(message);
        });
        
        // Listen for media messages
        this.connection.on("ReceiveMediaMessage", (mediaData) => {
            console.log("New media message:", mediaData);
            this.handleMediaMessage(mediaData);
        });
        
        // Listen for chat status changes
        this.connection.on("ChatStatusChanged", (chatId, status) => {
            console.log(\`Chat \${chatId} status: \${status}\`);
            this.handleChatStatusChange(chatId, status);
        });
        
        // Listen for message status changes
        this.connection.on("MessageStatusChanged", (messageId, status) => {
            console.log(\`Message \${messageId} status: \${status}\`);
            this.handleMessageStatusChange(messageId, status);
        });
        
        // Listen for new chat creation
        this.connection.on("ChatCreated", (chatDTO) => {
            console.log("New chat created:", chatDTO);
            this.handleNewChat(chatDTO);
        });
    }
    
    // Step 4: Send messages
    async sendTextMessage(chatId, message) {
        await this.connection.invoke("SendMessage", chatId, message);
    }
    
    async sendMediaMessage(chatId, mediaDTO) {
        await this.connection.invoke("SendMediaMessage", chatId, mediaDTO);
    }
    
    // Event handlers
    handleTextMessage(message) {
        // Process incoming text message
        // message.id, message.chatId, message.message, message.phone, etc.
    }
    
    handleMediaMessage(mediaData) {
        // Process incoming media message
        // mediaData.media.uri, mediaData.media.name, etc.
    }
    
    handleChatStatusChange(chatId, status) {
        // Handle chat status changes
        // status: 0=New, 1=Processing, 2=Ended
    }
    
    handleMessageStatusChange(messageId, status) {
        // Handle message status changes
        // status: 0=New, 1=Success, 2=Rejected, 3=Read, etc.
    }
    
    handleNewChat(chatDTO) {
        // Handle new chat creation
        // chatDTO.messSource, chatDTO.client, etc.
    }
}

// Usage example
const client = new ChathubClient();

// Initialize connection
await client.getToken('your_login', 'your_password');
await client.connect();

// Send a message
await client.sendTextMessage(123, "Hello from Chathub!");

// Send media message
const mediaDTO = {
    Name: "image.jpg",
    Format: "jpg", 
    DataBase64: "base64EncodedString",
    Type: 1 // MediaType.Photo
};
await client.sendMediaMessage(123, mediaDTO);`);

// Methods
const connectToHub = async () => {
  isConnecting.value = true;

  try {
    // Simulate connection
    await new Promise(resolve => setTimeout(resolve, 2000));
    isConnected.value = true;

    // Add connection message
    demoMessages.value.push({
      id: Date.now(),
      text: t('chathub.demo.connectedMessage'),
      type: 'system',
      time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    });

    scrollToBottom();
  } catch (error) {
    console.error('Connection failed:', error);
  } finally {
    isConnecting.value = false;
  }
};

const sendMessage = () => {
  if (!newMessage.value.trim() || !isConnected.value) return;

  const message = {
    id: Date.now(),
    text: newMessage.value,
    type: 'user',
    time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
  };

  demoMessages.value.push(message);
  newMessage.value = '';

  // Simulate bot response
  setTimeout(() => {
    demoMessages.value.push({
      id: Date.now() + 1,
      text: t('chathub.demo.botResponse'),
      type: 'bot',
      time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    });
    scrollToBottom();
  }, 1000);

  scrollToBottom();
};

const scrollToBottom = () => {
  nextTick(() => {
    if (chatMessages.value) {
      chatMessages.value.scrollTop = chatMessages.value.scrollHeight;
    }
  });
};

const showDemoCode = () => {
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
};

const copyCode = async () => {
  try {
    await navigator.clipboard.writeText(apiExamples.value[activeExample.value].code);
    // Show success message
  } catch (error) {
    console.error('Failed to copy code:', error);
  }
};

const copyDemoCode = async () => {
  try {
    await navigator.clipboard.writeText(demoCode.value);
    // Show success message
  } catch (error) {
    console.error('Failed to copy code:', error);
  }
};

const downloadExample = () => {
  const example = apiExamples.value[activeExample.value];
  const blob = new Blob([example.code], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `chathub-${example.title.toLowerCase().replaceAll(/\s+/g, '-')}.cs`;
  a.click();
  URL.revokeObjectURL(url);
};

const downloadDemoCode = () => {
  const blob = new Blob([demoCode.value], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'chathub-client-example.cs';
  a.click();
  URL.revokeObjectURL(url);
};

const downloadSDK = () => {
  // Create SDK package
  const sdkData = {
    version: '1.0.0',
    files: [
      'ChathubClient.cs',
      'ChatSource.cs',
      'README.md',
      'Examples/'
    ]
  };

  const blob = new Blob([JSON.stringify(sdkData, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'chathub-sdk.json';
  a.click();
  URL.revokeObjectURL(url);
};

const testExample = () => {
  // Simulate API test
  console.log('Testing API example:', apiExamples.value[activeExample.value].title);
};

// SEO
useHead({
  titleTemplate: () => t("seo.layouts./chathub.title"),
  meta: [
    { name: "description", content: () => t("seo.layouts./chathub.description") },
    { name: "keywords", content: "Chathub API, REST API, chat integration, Viber, Telegram, WhatsApp, SignalR, real-time chat, omnichannel communication, SMSBAT" },
    { name: "robots", content: "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" },
    { property: "og:title", content: () => t("seo.layouts./chathub.title") },
    { property: "og:description", content: () => t("seo.layouts./chathub.description") },
    { property: "og:type", content: "website" },
    { property: "og:site_name", content: "SMSBAT" },
    { property: "og:image", content: "https://cdn-web.smsbat.com/uploads/2024/chathub-api.png" },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:image:type", content: "image/png" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:site", content: "@smsbatcom" },
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "Chathub API",
        "description": t("seo.layouts./chathub.description"),
        "url": "https://smsbat.com/chathub",
        "applicationCategory": "DeveloperApplication",
        "operatingSystem": "Web",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "EUR",
          "description": "Free tier available"
        },
        "provider": {
          "@type": "Organization",
          "name": "SMSBAT",
          "url": "https://smsbat.com"
        },
        "featureList": [
          "REST API Communication",
          "Multi-Platform Support",
          "Easy Integration",
          "AI Agent Integration",
          "Advanced Analytics",
          "Scalable Architecture"
        ]
      })
    },
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "TechArticle",
        "headline": t("seo.layouts./chathub.title"),
        "description": t("seo.layouts./chathub.description"),
        "author": {
          "@type": "Organization",
          "name": "SMSBAT"
        },
        "publisher": {
          "@type": "Organization",
          "name": "SMSBAT",
          "logo": {
            "@type": "ImageObject",
            "url": "https://smsbat.com/logo.svg"
          }
        },
        "datePublished": "2024-01-01",
        "dateModified": "2024-01-01"
      })
    }
  ]
});

onMounted(() => {
  const el = document.querySelector("body")
  el?.scrollIntoView()
  if (globalThis.location.hash) {
    const el = document.querySelector(globalThis.location.hash)
    if (el) {
      setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth' })
      }, 500)
    }
  }
})
</script>

<style lang="scss" scoped>
.main-banner .banner-text {
  margin-left: 0;
  align-self: center;
  max-width: 700px;
}

.main-banner-content {
  padding: 0;
}

@media (min-width: 768px) {
  .main-banner .banner-text {
    max-width: 500px;
  }
}

@media (min-width: 1170px) {
  .main-banner .banner-text {
    max-width: 500px;
  }
}

@media (min-width: 1300px) {
  .main-banner .banner-text {
    margin-left: calc((100% - 1300px) / 2);
    max-width: 700px;
  }
}

.chat-demo {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  height: 400px;
  display: flex;
  flex-direction: column;
}

.chat-header {
  background: #f8f9fa;
  padding: 15px 20px;
  border-bottom: 1px solid #e9ecef;
}

.chat-status {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;

  &.connected {
    color: #28a745;
  }

  &.disconnected {
    color: #dc3545;
  }
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
}

.chat-messages {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.message {
  display: flex;

  &.user {
    justify-content: flex-end;

    .message-content {
      background: #4f46e5;
      color: white;
    }
  }

  &.bot {
    justify-content: flex-start;

    .message-content {
      background: #f8f9fa;
      color: #333;
    }
  }

  &.system {
    justify-content: center;

    .message-content {
      background: #e3f2fd;
      color: #0d47a1;
      font-style: italic;
    }
  }
}

.message-content {
  max-width: 70%;
  padding: 10px 15px;
  border-radius: 18px;
  position: relative;
}

.message-text {
  margin-bottom: 5px;
}

.message-time {
  font-size: 0.8rem;
  opacity: 0.7;
}

.chat-input {
  padding: 15px 20px;
  border-top: 1px solid #e9ecef;
  display: flex;
  gap: 10px;

  input {
    flex: 1;
    padding: 10px 15px;
    border: 1px solid #ddd;
    border-radius: 20px;
    outline: none;

    &:focus {
      border-color: #4f46e5;
    }

    &:disabled {
      background: #f8f9fa;
      cursor: not-allowed;
    }
  }

  button {
    padding: 10px 20px;
    background: #4f46e5;
    color: white;
    border: none;
    border-radius: 20px;
    cursor: pointer;

    &:disabled {
      background: #ccc;
      cursor: not-allowed;
    }
  }
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-top: 40px;
}

.feature-card {
  text-align: center;
  padding: 30px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
}

.feature-icon {
  width: 60px;
  height: 60px;
  margin: 0 auto 20px;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.feature-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 15px;
}

.feature-description {
  color: #718096;
  line-height: 1.6;
}

.sources-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-top: 40px;
}

.source-card {
  background: white;
  border-radius: 12px;
  padding: 25px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  }

  &.coming-soon {
    opacity: 0.6;
  }
}

.source-icon {
  width: 50px;
  height: 50px;
  margin: 0 auto 15px;
  border-radius: 10px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.source-name {
  font-size: 1.1rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 10px;
}

.status-badge {
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;

  &.available {
    background: #d4edda;
    color: #155724;
  }

  &.coming-soon {
    background: #fff3cd;
    color: #856404;
  }
}

.api-examples {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.api-tabs {
  display: flex;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.api-tab {
  flex: 1;
  padding: 15px 20px;
  background: none;
  border: none;
  cursor: pointer;
  font-weight: 600;
  color: #6c757d;
  transition: all 0.3s ease;

  &.active {
    background: white;
    color: #4f46e5;
    border-bottom: 2px solid #4f46e5;
  }

  &:hover:not(.active) {
    background: #e9ecef;
  }
}

.api-content {
  padding: 30px;
}

.api-description {
  margin-bottom: 25px;

  h4 {
    font-size: 1.3rem;
    font-weight: 700;
    color: #2d3748;
    margin-bottom: 10px;
  }

  p {
    color: #718096;
    line-height: 1.6;
  }
}

.code-block {
  background: #f8f9fa;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 25px;
}

.code-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background: #e9ecef;
  border-bottom: 1px solid #dee2e6;
}

.code-language {
  font-weight: 600;
  color: #495057;
}

.copy-button {
  background: #4f46e5;
  color: white;
  border: none;
  padding: 5px 12px;
  border-radius: 4px;
  font-size: 0.8rem;
  cursor: pointer;

  &:hover {
    background: #5a67d8;
  }
}

.code-block pre {
  margin: 0;
  padding: 20px;
  overflow-x: auto;

  code {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 0.9rem;
    line-height: 1.5;
    color: #2d3748;
  }
}

.api-actions {
  display: flex;
  gap: 15px;
}

.integration-steps {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.step-item {
  display: flex;
  gap: 20px;
  align-items: flex-start;
  max-width: 100dvw;
  overflow: auto;
}

.step-number {
  width: 40px;
  height: 40px;
  background: #4f46e5;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  flex-shrink: 0;
}

.step-content {
  flex: 1;
}

.step-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 10px;
}

.step-description {
  color: #718096;
  line-height: 1.6;
  margin-bottom: 15px;
}

.step-code {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 15px;

  pre {
    margin: 0;

    code {
      font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
      font-size: 0.9rem;
      color: #2d3748;
    }
  }
}

.bg-gradient {
  background: linear-gradient(135deg, #4f46e5 0%, #764ba2 100%);
  color: white;

  .section-head-title {
    color: white;
  }

  .text-gray-600 {
    color: rgba(255, 255, 255, 0.8);
  }
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  max-width: 800px;
  width: 90%;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 30px;
  border-bottom: 1px solid #e9ecef;

  h3 {
    font-size: 1.3rem;
    font-weight: 700;
    color: #2d3748;
  }
}

.close-button {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;

  &:hover {
    color: #495057;
  }
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 30px;
}

.demo-code {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;

  pre {
    margin: 0;
    overflow-x: auto;

    code {
      font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
      font-size: 0.9rem;
      line-height: 1.5;
      color: #2d3748;
    }
  }
}

.modal-footer {
  display: flex;
  gap: 15px;
  padding: 20px 30px;
  border-top: 1px solid #e9ecef;
  justify-content: flex-end;
}

@media (max-width: 768px) {
  .bottom-banner {
    margin-bottom: -40px;
  }

  .main-banner-content {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .chat-demo {
    height: 350px;
  }

  .features-grid {
    grid-template-columns: 1fr;
  }

  .sources-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .api-tabs {
    flex-direction: column;
  }

  .api-actions {
    flex-direction: column;
  }

  .step-item {
    flex-direction: column;
    text-align: center;
  }

  .modal-content {
    width: 95%;
    margin: 20px;
  }

  .modal-header,
  .modal-body,
  .modal-footer {
    padding: 20px;
  }
}

@media (max-width: 480px) {
  .sources-grid {
    grid-template-columns: 1fr;
  }

  .chat-demo {
    height: 300px;
  }

  .pricing-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .pricing-card {
    padding: 20px;

    &.popular {
      transform: none;

      &:hover {
        transform: translateY(-5px);
      }
    }
  }

  .plan-price .price {
    font-size: 2rem !important;
  }
}

// Pricing Section Styles
.pricing-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
  margin-top: 40px;
}

.pricing-card {
  background: white;
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  position: relative;
  transition: all 0.3s ease;
  border: 2px solid transparent;

  @media (max-width: 768px) {
    max-width: 95%;
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }

  &.popular {
    border-color: #4f46e5;

    @media (min-width: 768px) {
      transform: scale(1.05);
    }

    &:hover {
      transform: scale(1.05) translateY(-5px);
    }
  }
}

.popular-badge {
  position: absolute;
  top: -15px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #4f46e5, #764ba2);
  color: white;
  padding: 8px 20px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
}

.plan-header {
  text-align: center;
  margin-bottom: 30px;
}

.plan-name {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 15px;
}

.plan-price {
  margin-bottom: 15px;

  .price {
    font-size: 3rem;
    font-weight: 800;
    color: #4f46e5;
    display: block;
  }

  .period {
    font-size: 1rem;
    color: #718096;
  }
}

.plan-description {
  color: #718096;
  font-size: 1rem;
  line-height: 1.5;
}

.plan-features {
  margin-bottom: 30px;

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  li {
    padding: 12px 0;
    border-bottom: 1px solid #f7fafc;
    color: #718096;
    line-height: 1.5;

    &:last-child {
      border-bottom: none;
    }
  }

}

.plan-footer {
  text-align: center;
}

// FAQ Section Styles
.faq-list {
  max-width: 800px;
  margin: 0 auto;
}

.faq-item {
  border: 1px solid #e9ecef;
  border-radius: 10px;
  margin-bottom: 15px;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    border-color: #4f46e5;
  }
}

.faq-question {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background: #f8f9fa;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #e9ecef;
  }

  h4 {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
    color: #2d3748;
  }

  .faq-toggle {
    font-size: 1.5rem;
    color: #4f46e5;
    font-weight: bold;
    transition: transform 0.3s ease;
  }
}

.faq-answer {
  padding: 20px;
  background: white;

  p {
    margin: 0;
    color: #718096;
    line-height: 1.6;
  }
}
</style>